import { useState } from 'react';
import Seo from '@/components/Seo';
import { Breadcrumb } from '@/components/layout/Breadcrumb';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { ToolLongForm } from '@/components/tools/ToolLongForm';
import { ToolFAQ } from '@/components/tools/ToolFAQ';
import { AdSlot } from '@/components/ads/AdSlot';
import { checkPageSpeed } from '@/lib/api';
import { humanBytes } from '@/lib/download';
import {
    AlertCircle,
    CheckCircle2,
    Gauge,
    Loader2,
    ShieldCheck,
    TriangleAlert,
    Zap,
} from 'lucide-react';
import { toast } from 'sonner';

const FAQS = [
    { q: 'How is this different from PageSpeed Insights?', a: 'This is a lightweight probe from our servers, not a full Lighthouse audit. It’s useful for a quick, honest measurement of TTFB, total time, size and compression. For a full Core Web Vitals audit, run Chrome’s built-in Lighthouse on the URL.' },
    { q: 'Why did the total time differ from what I see in my browser?', a: 'We measure server round-trip only — not JavaScript execution, image loading, or layout. Your browser experience includes all of those, so it can feel slower even when the origin responds fast.' },
    { q: 'Do you visit the URL more than once?', a: 'No. Each check is a single, one-shot request. That means numbers can vary between runs — run 2–3 times if you want an average.' },
    { q: 'Is my URL logged?', a: 'The URL is used once for the check and is not stored, logged, or shared.' },
    { q: 'Does it work with any URL?', a: 'Any public HTTP(S) URL. Private or localhost URLs are blocked for safety.' },
];

const HOW = [
    'Paste any public URL (with or without “https://”) and press Check. Our server makes a single, careful GET request from a data center machine, times how long the origin took to send the first byte, then finishes reading up to 4 MB of the response and reports the total transfer time.',
    'We also inspect the response headers to check whether compression is enabled (gzip / brotli), whether the response has any cache-control directives, what web server it advertises, and how many redirects it went through to get to the final URL. All of these signals are the small performance wins that add up to a fast site.',
    'Finally, we hand you a short list of contextual tips based on what we saw. If the response wasn’t compressed, that’s tip #1. If the origin took more than 600 ms to respond, that’s tip #2. The idea is to give you a starting point for improvements, not a scary red audit report.',
    'Because it’s a lightweight probe, results are best used as a quick pulse-check — not as a substitute for a full Chrome Lighthouse audit. Run both to get a complete picture.',
];
const BENEFITS = [
    'Site speed compounds. Every second you shave off your load time raises conversion rates, cuts bounce rates, and quietly climbs your search rankings. Being able to check any URL — your competitor’s, a client’s, one you’re considering acquiring — without installing anything is a small superpower.',
    'For teams shipping frequently, a fast independent check tells you if your changes made the site faster or slower. Run it before and after a deploy; if TTFB moved, ask why.',
    'For SEO consultants, journalists, and freelancers, a public URL check is a starting point for a bigger conversation with a client. “Your homepage takes 3.2 seconds to first byte” is a very concrete opener that leads naturally into the value of a rebuild or a CDN.',
];
const TIPS = [
    'Run the check 2–3 times and average the results — single measurements are noisy.',
    'Compare a naked origin URL (e.g. https://example.com) with your CDN URL to see the CDN’s effect.',
    'Pair this with Chrome Lighthouse for a full Core Web Vitals audit.',
];

function Verdict({ verdict, totalMs }) {
    const map = {
        fast: {
            label: 'Fast',
            icon: Zap,
            klass: 'bg-emerald-100 text-emerald-700 border-emerald-200',
        },
        ok: {
            label: 'Okay',
            icon: CheckCircle2,
            klass: 'bg-amber-100 text-amber-700 border-amber-200',
        },
        slow: {
            label: 'Slow',
            icon: TriangleAlert,
            klass: 'bg-rose-100 text-rose-700 border-rose-200',
        },
    };
    const conf = map[verdict] || map.ok;
    const Icon = conf.icon;
    return (
        <div
            className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-sm font-medium ${conf.klass}`}
        >
            <Icon className="w-4 h-4" /> {conf.label} · {totalMs} ms total
        </div>
    );
}

export default function PageSpeedPage() {
    const [url, setUrl] = useState('');
    const [status, setStatus] = useState('idle');
    const [error, setError] = useState('');
    const [result, setResult] = useState(null);

    const run = async () => {
        if (!url.trim()) return;
        setStatus('loading');
        setError('');
        setResult(null);
        try {
            const data = await checkPageSpeed(url.trim());
            setResult(data);
            setStatus('done');
            toast.success('Page speed check complete.');
        } catch (err) {
            const msg = err?.response?.data?.detail || err?.message || 'Something went wrong.';
            setError(msg);
            setStatus('error');
            toast.error(msg);
        }
    };

    const structured = {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: 'UtilityTool — Page Speed Checker',
        applicationCategory: 'Utility',
        operatingSystem: 'Any',
        offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    };

    return (
        <>
            <Seo
                title="Page Speed Checker"
                description="Measure the loading performance of any public webpage — TTFB, total time, compression, cache headers, and quick tips to make it faster."
                canonical="/tools/page-speed"
                structuredData={structured}
            />
            <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-14">
                <Breadcrumb items={[{ label: 'Tools', to: '/tools' }, { label: 'Page Speed Checker' }]} />
                <div className="mt-4 flex items-start gap-4">
                    <span className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--primary))]">
                        <Gauge className="w-6 h-6" />
                    </span>
                    <div>
                        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">Page Speed Checker</h1>
                        <p className="mt-2 text-slate-600 max-w-2xl">
                            Measure TTFB, total load time, and compression for any public
                            webpage. Get actionable tips to make it faster.
                        </p>
                        <div className="mt-3 flex flex-wrap gap-2">
                            <Badge variant="secondary" className="bg-slate-100 text-slate-700">SEO</Badge>
                            <Badge variant="secondary" className="bg-slate-100 text-slate-700">No signup</Badge>
                        </div>
                    </div>
                </div>

                <Card className="mt-8 p-5 sm:p-6 border-border/80">
                    <div className="flex flex-col sm:flex-row gap-3">
                        <div className="flex-1">
                            <Label htmlFor="ps-url">URL</Label>
                            <Input
                                id="ps-url"
                                data-testid="tool-ps-url"
                                value={url}
                                onChange={(e) => setUrl(e.target.value)}
                                placeholder="example.com or https://example.com/page"
                                className="mt-1.5"
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') run();
                                }}
                            />
                        </div>
                        <div className="sm:self-end">
                            <Button
                                onClick={run}
                                disabled={!url.trim() || status === 'loading'}
                                data-testid="tool-ps-run"
                                className="h-11 bg-slate-900 hover:bg-slate-800 text-white w-full sm:w-auto"
                            >
                                {status === 'loading' ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Checking…
                                    </>
                                ) : (
                                    'Check speed'
                                )}
                            </Button>
                        </div>
                    </div>

                    {status === 'error' && error ? (
                        <Alert variant="destructive" className="mt-6" data-testid="tool-ps-error">
                            <AlertCircle className="w-4 h-4" />
                            <AlertTitle>We couldn’t check that URL</AlertTitle>
                            <AlertDescription>{error}</AlertDescription>
                        </Alert>
                    ) : null}

                    {result ? (
                        <div className="mt-6" data-testid="tool-ps-result">
                            <div className="flex items-center flex-wrap gap-3">
                                <Verdict verdict={result.verdict} totalMs={result.total_ms} />
                                <span className="text-sm text-slate-500">
                                    HTTP {result.status_code} · {result.final_url}
                                </span>
                            </div>
                            <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                                <Stat label="TTFB" value={`${result.ttfb_ms} ms`} testid="ps-ttfb" />
                                <Stat label="Total" value={`${result.total_ms} ms`} testid="ps-total" />
                                <Stat label="Size" value={humanBytes(result.content_bytes)} testid="ps-size" />
                                <Stat label="Compressed" value={result.gzip ? 'Yes' : 'No'} testid="ps-gzip" />
                                <Stat label="Redirects" value={String(result.redirects)} testid="ps-redirects" />
                                <Stat label="Content-Type" value={(result.content_type || '—').split(';')[0]} testid="ps-ctype" />
                                <Stat label="Server" value={result.server || '—'} testid="ps-server" />
                                <Stat label="Cache" value={result.cache_control || '—'} testid="ps-cache" />
                            </div>
                            {result.tips && result.tips.length > 0 ? (
                                <div className="mt-6 rounded-xl border border-border/70 bg-[hsl(var(--surface-2))] p-4">
                                    <p className="text-sm font-semibold text-slate-900">What to try next</p>
                                    <ul className="mt-2 list-disc pl-5 space-y-1 text-sm text-slate-700">
                                        {result.tips.map((t, i) => (
                                            <li key={i}>{t}</li>
                                        ))}
                                    </ul>
                                </div>
                            ) : null}
                        </div>
                    ) : null}
                </Card>

                <div className="mt-6 rounded-xl border border-border/70 bg-[hsl(var(--surface-2))] p-4">
                    <div className="flex items-center gap-2 text-slate-800">
                        <ShieldCheck className="w-4 h-4 text-[hsl(var(--primary))]" />
                        <span className="text-sm font-semibold">We don’t store URLs</span>
                    </div>
                    <p className="mt-1 text-sm text-slate-600">
                        Your URL is used once, live, and is never logged or shared.
                    </p>
                </div>

                <ToolLongForm howItWorks={HOW} benefits={BENEFITS} tips={TIPS} />
                <AdSlot placement="tool" />
                <ToolFAQ items={FAQS} />
            </section>
        </>
    );
}

function Stat({ label, value, testid }) {
    return (
        <div className="rounded-lg border border-border/70 bg-white p-3" data-testid={testid}>
            <p className="text-xs text-slate-500">{label}</p>
            <p className="mt-0.5 text-sm font-semibold text-slate-900 truncate" title={value}>{value}</p>
        </div>
    );
}
